import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import Service, { inject as service } from '@ember/service';

export default Service.extend({
  errorTracker: service('error-tracker'),
  heartbeatHybrid: service('state-manager-hybrids/heartbeat-state-manager-hybrid'),
  CEW: service('customEndpointWrapper'),

  //////////////////////////////////
  // Generic Utility Functions
  //////////////////////////////////

  getThreadMetaUserArray(thread, user) {
    const userInfoArray = thread.etc.relationships.users;
    const userInfoIndex = userInfoArray.findIndex((userInfo) => parseInt(userInfo.user_id) === parseInt(user.id));
    const userInfo = userInfoIndex !== -1
      ? userInfoArray[userInfoIndex]
      : null;

    return [userInfo, userInfoIndex];
  },

  updateThreadMetaPropertyStatus: function(thread, user, property, status) {
    let userInfoCollection = this.getThreadMetaUserArray(thread, user);
    let userInfoIndex = userInfoCollection[1];

    if (userInfoIndex > -1) {
      thread.set(`etc.relationships.users.${userInfoIndex}.${property}`, status);
    }
  },

  threadHasBeenReadByUser: function(thread, user) {
    let userInfoCollection = this.getThreadMetaUserArray(thread, user);
    let userInfo = userInfoCollection[0];

    if (!userInfo) {
      return true;
    }

    let lastRead = new Date(userInfo.last_read_at);
    let lastUpdated;

    if (thread.get('messages.length')) {
      const messages = Array.isArray(thread.messages)
        ? thread.messages
        : thread.messages.toArray();

      [...messages].sort((a,b) => {
        if(a.createdAt > b.createdAt){
          return -1;
        } else if (a.createdAt < b.createdAt){
          return 1;
        } else {
          return 0;
        }
      });

      lastUpdated = messages[0]?.createdAt;

    } else {
      lastUpdated = thread.get('updatedAt');
    }

    return (lastRead && lastRead.getTime() >= lastUpdated.getTime() );
  },

  //////////////////////////////////
  // lastReadAt Pseudo Prop
  //////////////////////////////////

  updateThreadMetaLastReadAtStatus: function(thread, user, markUnread) {
    let property = 'last_read_at';
    let status;

    if(markUnread) {
      status = null;
    } else {
      status = new Date().toISOString();
    }

    this.updateThreadMetaPropertyStatus(thread, user, property, status);
    return status;
  },

  initializeThreadLastReadAtStatus(thread, user) {
    const oldDate = thread.get('lastReadAt');
    const [userData] = this.getThreadMetaUserArray(thread, user);

    if (!userData) {
      // maximum possible date in the future, should make it impossible for the thread to be unread
      thread.set('lastReadAt', new Date(8640000000000000));
      return;
    }

    const newDate = new Date(userData.last_read_at);

    // The timestamp should only ever go forward, not backward.
    if (oldDate && newDate.getTime() < oldDate.getTime()) return;

    thread.set('lastReadAt', newDate);
  },

  //////////////////////////////////
  // unread Pseudo Prop
  //////////////////////////////////


  initializeThreadUnreadStatus(thread, user) {
    if (this.threadHasBeenReadByUser(thread, user)) {
      thread.set('unread', false);
    } else {
      thread.set('unread', true);
    }
  },

  markThreadUnreadAndSyncMeta(thread, user) {
    // We need to locally update the Inbox/Archive unread counts
    // to keep the heartbeat service in sync with local activity until
    // the next heartbeat poll.

    if (thread.get('unread') === false) {
      this._updateHeartbeatUnreadCount(thread, true);
    }

    this.updateThreadMetaLastReadAtStatus(thread, user, true);
    this.initializeThreadLastReadAtStatus(thread, user);
    thread.set('unread', true);
  },

  //////////////////////////////////
  // archivedAt Pseudo Prop
  //////////////////////////////////

  updateThreadMetaArchivedStatus: function(thread, user, archiveIt) {
    let property = 'archived_at';
    let status;

    if(archiveIt) {
      status = new Date().toISOString();
    } else {
      status = null;
    }

    this.updateThreadMetaPropertyStatus(thread, user, property, status);
    return status;
  },

  initializeThreadArchivedStatus(thread, user) {
    let userInfoCollection = this.getThreadMetaUserArray(thread, user);
    let userInfo = userInfoCollection[0];

    let archivedAt = (userInfo && userInfo.archived_at) ? new Date(userInfo.archived_at) : null;
    thread.set('archivedAt', archivedAt);
  },

  archiveThreadAndSyncMeta(thread, user) {
    let status = this.updateThreadMetaArchivedStatus(thread, user, true);
    thread.set('archivedAt', new Date(status));
  },

  unarchiveThreadAndSyncMeta(thread, user) {
    let status = this.updateThreadMetaArchivedStatus(thread, user, false);
    thread.set('archivedAt', status);
  },

  //////////////////////////////////
  // watchedBy Pseudo Prop
  //////////////////////////////////

  initializeThreadWatchedByStatus(thread) {
    let threadWatcherIds = A();

    thread.get('etc.relationships.users').forEach((item) => {
      if(item.role === 'watcher' && !item.deleted_at) {
        threadWatcherIds.pushObject(item.user_id.toString());
      }
    });

    thread.set('watchedBy', threadWatcherIds);
  },

  updateThreadMetaWatchedByStatus(thread, user, addWatcher) {

    let threadUsers = thread.get('etc.relationships.users');
    let date = new Date().toISOString();

    if(addWatcher) {
      threadUsers.pushObject({
        created_at: date,
        updated_at: date,
        thread_id: thread.get('id').toString(),
        user_id: user.get('id').toString(),
        role: 'watcher'
      });
    } else {
      const userArray = this.getThreadMetaUserArray(thread, user)[0];
      const indexToRemove = threadUsers.indexOf(userArray);

      if (indexToRemove !== -1) {
        threadUsers.splice(indexToRemove, 1);
      }
    }
  },

  addThreadWatcherAndSyncMeta(thread, user) {
    this.updateThreadMetaWatchedByStatus(thread, user, true);
    thread.get('watchedBy').pushObject(user.get('id').toString());
  },

  removeThreadWatcherAndSyncMeta(thread, user) {
    this.updateThreadMetaWatchedByStatus(thread, user, false);
    thread.get('watchedBy').removeObject(user.get('id').toString());
  },

  initializeThreadPseudoProperties(thread, user) {
    this.initializeThreadLastReadAtStatus(...arguments);
    this.initializeThreadUnreadStatus(...arguments);
    this.initializeThreadArchivedStatus(...arguments);
    this.initializeThreadWatchedByStatus(thread);
  },

  //////////////////////////////////
  // Formats data for adding/removing users from a thread
  //////////////////////////////////

  buildAddRemoveUserData(userIdsAndRoles) {
    const userIdsAndRolesArray = Array.isArray(userIdsAndRoles)
      ? userIdsAndRoles
      : userIdsAndRoles.toArray();

    const userData = userIdsAndRolesArray.reduce((accum, userInfo) => {
      accum.push({
        id: userInfo.id,
        type: 'user',
        role: userInfo.role
      });

      return accum;
    }, []);

    return this.get('CEW').format(userData);
  },

  //////////////////////////////////
  // Wraps the thread.markRead behavior in a convenience function
  // that automatically updates its meta and initializes its
  // pseudo properties.  Should be used instead of the local metaReadAndSync
  //////////////////////////////////

  actionMarkThreadRead(thread, user, messageID) {
    const options = {
      message_id: messageID,
      data: {
        id: user.get('id'),
        type: 'user',
      },
    };

    return thread.markRead(options)
      .then((response) => {
        return this.updateThreadModel(thread, response.data, user);
      })
      .catch((error) => {
        const message = `actionMarkThreadRead -- Thread ID: ${thread.get('id')}`;
        this.get('errorTracker').error(message, this, error);
        throw error;
      });
  },

  /**
   * Updates the thread model using a response from the server.
   */
  updateThreadModel(threadModel, threadResponse, user) {
    threadModel.set(
      'etc.relationships.users',
      threadResponse.attributes.etc.relationships.users,
    );

    // Running this in the 'afterRender' phase keeps all the UI changes in sync
    scheduleOnce('afterRender', this, function () {
      this.initializeThreadPseudoProperties(threadModel, user);

      /*
       * Locally update the Inbox/Archive unread counts to keep the heartbeat service in sync with
       * local activity until the next heartbeat poll.
       */
      if (threadModel.get('unread')) {
        this._updateHeartbeatUnreadCount(threadModel, false);
      }
    });

    return threadModel;
  },

  //////////////////////////////////
  // Utility function for managing the UserState/Heartbeat
  // messages unread count locally, while waiting for the next
  // heartbeat
  //////////////////////////////////

  _updateHeartbeatUnreadCount(thread, increment = true) {
    let heartbeatHybrid = this.get('heartbeatHybrid');
    let property = thread.get('archivedAt') ? 'threadsUnreadArchive' : 'threadsUnreadInbox';

    if (increment) {
      heartbeatHybrid.set(property, heartbeatHybrid[property] + 1);
    } else {
      heartbeatHybrid.set(property, heartbeatHybrid[property] - 1);
    }
  },

});
